
import React, { createContext, useContext, useState, useEffect } from 'react';

interface GDPRContextType {
  hasConsented: boolean;
  analyticsConsent: boolean;
  marketingConsent: boolean;
  functionalConsent: boolean;
  showBanner: boolean;
  acceptAll: () => void;
  acceptEssential: () => void;
  updateConsents: (consents: Partial<ConsentSettings>) => void;
  showSettings: () => void;
  hideBanner: () => void;
}

interface ConsentSettings {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const GDPRContext = createContext<GDPRContextType | undefined>(undefined);

export const useGDPR = () => {
  const context = useContext(GDPRContext);
  if (!context) {
    throw new Error('useGDPR must be used within a GDPRProvider');
  }
  return context;
};

export const GDPRProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasConsented, setHasConsented] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [functionalConsent, setFunctionalConsent] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Vérifier les consentements stockés
    const storedConsent = localStorage.getItem('gdpr-consent');
    if (storedConsent) {
      const consent = JSON.parse(storedConsent);
      setHasConsented(true);
      setAnalyticsConsent(consent.analytics || false);
      setMarketingConsent(consent.marketing || false);
      setFunctionalConsent(consent.functional || false);
      setShowBanner(false);
    } else {
      // Afficher la bannière si aucun consentement n'a été donné
      setShowBanner(true);
    }
  }, []);

  const saveConsents = (consents: ConsentSettings) => {
    localStorage.setItem('gdpr-consent', JSON.stringify({
      ...consents,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }));
  };

  const acceptAll = () => {
    const consents = { analytics: true, marketing: true, functional: true };
    setHasConsented(true);
    setAnalyticsConsent(true);
    setMarketingConsent(true);
    setFunctionalConsent(true);
    setShowBanner(false);
    saveConsents(consents);
  };

  const acceptEssential = () => {
    const consents = { analytics: false, marketing: false, functional: true };
    setHasConsented(true);
    setAnalyticsConsent(false);
    setMarketingConsent(false);
    setFunctionalConsent(true);
    setShowBanner(false);
    saveConsents(consents);
  };

  const updateConsents = (newConsents: Partial<ConsentSettings>) => {
    const consents = {
      analytics: newConsents.analytics ?? analyticsConsent,
      marketing: newConsents.marketing ?? marketingConsent,
      functional: newConsents.functional ?? functionalConsent,
    };
    
    setAnalyticsConsent(consents.analytics);
    setMarketingConsent(consents.marketing);
    setFunctionalConsent(consents.functional);
    setHasConsented(true);
    saveConsents(consents);
  };

  const showSettings = () => {
    setShowBanner(true);
  };

  const hideBanner = () => {
    setShowBanner(false);
  };

  return (
    <GDPRContext.Provider value={{
      hasConsented,
      analyticsConsent,
      marketingConsent,
      functionalConsent,
      showBanner,
      acceptAll,
      acceptEssential,
      updateConsents,
      showSettings,
      hideBanner
    }}>
      {children}
    </GDPRContext.Provider>
  );
};
